// src/admin/useAdminPacks.js
//
// Admin-side live readers for Firestore /packs and a pack's free/paid items.
// Read-only listeners; all writes go through the admin Cloud Functions. Mock
// mode (no backend) returns empty + a flag so the editor can show a notice.
import { useState, useEffect, useCallback, useContext } from 'react';
import { DraftContext } from './DraftContext.jsx';
import { collection, onSnapshot, getDocs } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';

export function useAdminPacks() {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (AUTH_MODE === 'mock' || !db) { setPacks([]); setLoading(false); return undefined; }
        setLoading(true);
        const unsub = onSnapshot(collection(db, 'packs'),
            (snap) => {
                const rows = [];
                snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
                rows.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
                setPacks(rows);
                setLoading(false);
            },
            (err) => { console.error('useAdminPacks:', err); setPacks([]); setLoading(false); });
        return () => unsub();
    }, []);

    return { packs, loading, backendAvailable: AUTH_MODE !== 'mock' && !!db };
}

// On-demand fetch of a pack's items for a scope ('freeItems' | 'paidItems').
//
// `enabled` (default true) gates whether the read is attempted AT ALL. The set-text
// reader (Slice 2) passes enabled=false for 'paidItems' until the user has an active
// entitlement, so a non-entitled session NEVER calls getDocs(paidItems) -- the gate
// is strict (not attempt-then-deny). All existing admin callers omit the arg and so
// keep the previous always-on behaviour.
//
// Returns add `error` (the read threw -- lets a caller show an inline error and
// distinguish a failed read from a legitimately empty scope) and `settled` (a read
// has completed at least once while enabled -- lets a caller render a loading state
// across the brief frame between `enabled` flipping true and the fetch starting,
// instead of flashing an empty/ready state). Both are ignored by callers that do not
// destructure them.
export function usePackItems(packId, scope, enabled = true) {
    const draft = useContext(DraftContext);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [settled, setSettled] = useState(false);

    const reload = useCallback(async () => {
        // Disabled / no backend: clear items, surface nothing, and DO NOT mark settled
        // (so a caller treats a not-yet-enabled scope as still-pending, not empty).
        if (AUTH_MODE === 'mock' || !db || !packId || !enabled) { setItems([]); setError(null); setLoading(false); return; }
        setSettled(false);
        setLoading(true);
        setError(null);
        try {
            const snap = await getDocs(collection(db, draft ? 'contentDrafts' : 'packs', packId, scope));
            const rows = [];
            snap.forEach((d) => rows.push({ id: d.id, ...d.data() }));
            rows.sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));
            setItems(rows);
        } catch (e) {
            console.error('usePackItems:', e);
            setItems([]);
            setError(e);
        } finally {
            setLoading(false);
            setSettled(true);
        }
    }, [packId, scope, enabled, draft]);

    useEffect(() => { reload(); }, [reload]);
    return { items, loading, error, settled, reload };
}
